import { Manager } from "socket.io-client";
import { inflate } from "pako";
import * as posconApi from "./api/posconApi";
import { deleteCpdlcSessions, deleteFlightplans, deleteSectorTracks, deleteTracks, resetAircraftState, setCpdlcDialogs, setCpdlcSession, setCpdlcSessionEligibility, setCpdlcSessions, setFlightplans, setSectorTracks, setStcaPairs, setSuppressedConflictPairs, setTerminatedCpdlcSessions, setTracks, terminateCpdlcSessions, updateCoordination, updateFlightplans, } from "./eramReact/slices/aircraftSlice.js";
import { setMetars } from "./eramReact/slices/weatherSlice.js";
import { setConnection, setSocketConnected } from "./eramReact/slices/connectionSlice.js";
import { removeAdvisories, setOwnedFavIds, setSaa, setSaaActivations, setSaaMap, setSectorSaaDisplay, setSectorSaaDisplayMap, updateAdvisories, } from "./eramReact/slices/commonSlice.js";
import { genericReject, } from "@poscon/shared-types/eram";
import { invoke } from "@tauri-apps/api/core";
import { listen } from "@tauri-apps/api/event";
const encoder = new TextEncoder();
export class EramMessageProcessingError extends Error {
    result;
    constructor(result) {
        super();
        this.result = result;
    }
}
export class EramHubConnection {
    url;
    manager = null;
    socket = null;
    client = null;
    role = "Observer";
    _sectorId = null;
    isActive = false;
    constructor(url) {
        this.url = url;
        this.registerEventHandlers();
    }
    async init() { }
    dispatch;
    getState;
    getPrefsetState;
    artccId = null;
    registerEventHandlers() {
        this.registerEventHandler("updateTracks", ([tracks]) => {
            this.dispatch?.(setTracks(tracks));
        });
        this.registerEventHandler("updateTracksBin", ([tracks]) => {
            this.dispatch?.(setTracks(tracks));
        });
        this.registerEventHandler("deleteTracks", ([trackIds]) => {
            this.dispatch?.(deleteTracks(trackIds));
        });
        this.registerEventHandler("updateSuppressedConflictPairs", ([pairs]) => {
            this.dispatch?.(setSuppressedConflictPairs(pairs.map((v) => v.trackIds)));
        });
        this.registerEventHandler("removeSectorTracks", ([trackIds]) => {
            this.dispatch?.(deleteSectorTracks(trackIds));
        });
        this.registerEventHandler("addFlightplans", ([flightplans]) => {
            this.dispatch?.(setFlightplans(flightplans));
        });
        this.registerEventHandler("updateFlightplans", ([flightplans]) => {
            this.dispatch?.(updateFlightplans(flightplans));
        });
        this.registerEventHandler("deleteFlightplans", ([ids]) => {
            this.dispatch?.(deleteFlightplans(ids));
        });
        this.registerEventHandler("updateCoordination", ([coordination]) => {
            this.dispatch?.(updateCoordination(coordination));
        });
        this.registerEventHandler("updateSectorTracks", ([sectorTracks]) => {
            this.dispatch?.(setSectorTracks(sectorTracks));
        });
        this.registerEventHandler("receiveStcaPairs", ([pairs]) => {
            this.dispatch?.(setStcaPairs(pairs.map((v) => v.trackIds)));
        });
        this.registerEventHandler("receiveCpdlcSessions", ([sessions]) => {
            this.dispatch?.(setCpdlcSessions(sessions));
        });
        this.registerEventHandler("receiveCpdlcSession", ([{ fpId, session }]) => {
            this.dispatch?.(setCpdlcSession({ id: fpId, session }));
        });
        this.registerEventHandler("receiveTerminatedCpdlcSessions", ([sessions]) => {
            this.dispatch?.(setTerminatedCpdlcSessions(sessions));
        });
        this.registerEventHandler("receiveCpdlcDialogs", ([data]) => {
            this.dispatch?.(setCpdlcDialogs(data));
        });
        this.registerEventHandler("receiveCpdlcSessionEligibilityStatusMap", ([eligibilityStatusMap]) => {
            this.dispatch?.(setCpdlcSessionEligibility(eligibilityStatusMap));
        });
        this.registerEventHandler("terminateCpdlcSessions", ([fpIds]) => {
            this.dispatch?.(terminateCpdlcSessions(fpIds));
        });
        this.registerEventHandler("deleteCpdlcSessions", ([fpIds]) => {
            this.dispatch?.(deleteCpdlcSessions(fpIds));
        });
        this.registerEventHandler("receiveMetarEntryMap", ([metarEntryMap]) => {
            this.dispatch?.(setMetars(metarEntryMap));
        });
        this.registerEventHandler("receiveAdvisories", ([advisories]) => {
            this.dispatch?.(updateAdvisories(advisories));
        });
        this.registerEventHandler("receiveAdvisory", ([advisory]) => {
            this.dispatch?.(updateAdvisories([advisory]));
        });
        this.registerEventHandler("removeAdvisories", ([advisories]) => {
            this.dispatch?.(removeAdvisories(advisories));
        });
        this.registerEventHandler("receiveSaaStatusList", ([list]) => {
            this.dispatch?.(setSaaMap(Object.fromEntries(list.map((saa) => [saa.saaId, saa]))));
        });
        this.registerEventHandler("receiveSaaStatus", ([saa]) => {
            this.dispatch?.(setSaa({ saaId: saa.saaId, saa }));
        });
        this.registerEventHandler("receiveSectorSaaDisplayMap", ([sectorSAADisplayMap]) => {
            this.dispatch?.(setSectorSaaDisplayMap(Object.fromEntries(sectorSAADisplayMap.map((saa) => [saa[0].saaId, saa[1]]))));
        });
        this.registerEventHandler("receiveSectorSaaDisplay", ([{ id, display }]) => {
            this.dispatch?.(setSectorSaaDisplay({ saaId: id.saaId, display }));
        });
        this.registerEventHandler("receiveSaaActivations", ([{ id, activations, forcedOff }]) => {
            this.dispatch?.(setSaaActivations({ saaId: id.saaId, activations, forcedOff }));
        });
        this.registerEventHandler("receiveOwnedFavIds", ([favs]) => {
            this.dispatch?.(setOwnedFavIds(favs));
        });
        this.registerEventHandler("receiveConnectionStatus", ([connection]) => {
            this._sectorId = connection.sectorId;
            this.isActive = connection.isActive;
            this.dispatch?.(setConnection(connection));
        });
        // getUiState is a server-initiated request/ack — the server calls emit_with_ack
        // and expects the prefset state back. Register it here so handleWtEvent can
        // respond; the Socket.IO path registers it directly on the socket instead.
        // @ts-ignore
        this.registerEventHandler("getUiState", () => {
            return this.getPrefsetState?.();
        });
    }
    initialized = false;
    usingWt = false;
    accessToken = null;
    async connectWt() {
        const artccId = this.artccId;
        const sectorId = this.sectorId;
        const role = this.role;
        const ns = `/eram.${artccId}`;
        await invoke("wt_connect", { ns, artccId, sectorId, role });
    }
    async handleWtEvent(e) {
        const payload = e.payload;
        const data = JSON.parse(payload.data);
        const request_id = payload.data_request_id;
        const eventHandler = this.eventHandlers[payload.event_name];
        // @ts-ignore
        const response = eventHandler?.([data]);
        if (response && typeof request_id === "number") {
            await invoke("nas_event_response", encoder.encode(JSON.stringify(response)), { headers: { id: request_id.toString(10) } });
        }
    }
    async initializeWt() {
        const _unlisten = await listen("nas_event", async (e) => {
            await this.handleWtEvent(e);
        });
        listen("wt_connected", async () => {
            this.onConnect();
        });
        listen("wt_disconnected", () => {
            this.onDisconnect();
        });
    }
    async initialize(artccId, client, sectorId, role) {
        this.accessToken = await posconApi.getAtcAccessToken();
        this.artccId = artccId;
        this._sectorId = sectorId;
        this.client = client;
        this.role = role;
        this.usingWt = window.__TAURI__ && await invoke("supports_wt").catch(() => false);
        if (this.usingWt) {
            await this.initializeWt();
        }
        else {
            await this.initializeSio(artccId, client, sectorId, role);
        }
    }
    async initializeSio(artccId, client, sectorId, role) {
        if (this.manager === null) {
            console.log("initializing artcc: ", artccId);
            const manager = new Manager(this.url, {
                transports: ["websocket", "webtransport"],
                // put things into auth because rust's server implementation doesn't support query
                query: {
                    client,
                    role,
                    sectorId,
                },
                path: "/socket.io",
                reconnection: true,
                reconnectionAttempts: 12,
                reconnectionDelay: 5e3,
                autoConnect: false,
            });
            this.manager = manager;
            const socket = manager.socket(`/eram.${artccId}`, {
                auth: {
                    token: this.accessToken,
                    client: this.client,
                    role,
                    artccId,
                    sectorId,
                },
            });
            socket.onAny((event, ...args) => {
                if (this.eventHandlers[event]) {
                    const eventHandlerArgs = args.map((arg) => arg instanceof Uint8Array || arg instanceof ArrayBuffer
                        ? JSON.parse(inflate(arg, { to: "string" }))
                        : arg);
                    // @ts-ignore
                    this.eventHandlers[event]?.(eventHandlerArgs);
                }
            });
            this.socket = socket;
            this.socket.on("connect_error", (err) => {
                // the reason of the error, for example "xhr poll error"
                console.log("eram hub connection error", err.message);
                if ("description" in err) {
                    // some additional description, for example the status code of the initial HTTP response
                    console.log(err.description);
                }
                if ("context" in err) {
                    // some additional context, for example the XMLHttpRequest object
                    console.log(err.context);
                }
            });
            this.socket.on("connect", () => {
                console.log(`connected with transport ${this.socket?.io.engine.transport.name}`);
                this.onConnect();
            });
            this.socket.on("disconnect", (reason, details) => {
                // the reason of the disconnection, for example "transport error"
                console.log("eram hub disconnected ", reason);
                if (details) {
                    // the low-level reason of the disconnection, for example "xhr post error"
                    console.log("message" in details ? details.message : "");
                    // some additional description, for example the status code of the HTTP response
                    console.log("description" in details ? details.description : "");
                    // some additional context, for example the XMLHttpRequest object
                    console.log("context" in details ? details.context : "");
                }
                this.onDisconnect();
            });
            socket.on("getUiState", (_, cb) => {
                const state = this.getPrefsetState?.();
                cb(state);
            });
        }
        this.initialized = true;
    }
    eventHandlers = {};
    registerEventHandler(event, callback) {
        this.eventHandlers[event] = callback;
    }
    removeEventHandler(event) {
        const handler = this.eventHandlers[event];
        if (handler) {
            // @ts-ignore
            this.socket?.off(event, handler);
        }
        delete this.eventHandlers[event];
    }
    async getValue(facility, filename, endpoint, signal) {
        const response = await fetch(`${import.meta.env.VITE_STRATUS_FACILITY_DATA_URL}/${endpoint}`, {
            signal,
            headers: {
                Authorization: this.accessToken ?? "",
            },
        });
        const data = (await response.json());
        return data;
    }
    async getValues(facility, dir, endpoint, idFactory, signal) {
        const response = await fetch(`${import.meta.env.VITE_STRATUS_FACILITY_DATA_URL}/${endpoint}`, {
            signal,
            headers: {
                Authorization: this.accessToken ?? "",
            },
        });
        const data = (await response.json());
        return data;
    }
    async getEDSMConfig(facility, signal) {
        return this.getValue(facility, "edsmConfig", `${facility}/eramEDSMConfig`, signal);
    }
    async getKSDConfig(facility, signal) {
        return this.getValue(facility, "ksdConfig", `${facility}/eramKSDConfig`, signal);
    }
    async getRPosConfig(facility, signal) {
        return this.getValue(facility, "rposConfig", `${facility}/eramRposConfig`, signal);
    }
    async getDposConfig(facility, signal) {
        return this.getValue(facility, "dposConfig", `${facility}/eramDposConfig`, signal);
    }
    async getEramConfig(facility, signal) {
        return this.getValue(facility, "eramConfig", `${facility}/eramConfig`, signal);
    }
    async getGeomapConfigs(facility, signal) {
        return this.getValues(facility, "geomapConfig", `${facility}/eramGeomapConfigs`, (v) => v.geomapId, signal);
    }
    async getEdstGeomapConfig(facility, signal) {
        return this.getValue(facility, "edstGeomapConfig", `${facility}/edstGeomapConfig`, signal);
    }
    async getBCGs(facility, signal) {
        return this.getValues(facility, "bcg", `${facility}/eramBCG`, (v) => v.name, signal);
    }
    async getFilterMenu(facility, signal) {
        return this.getValues(facility, "mapFilterMenu", `${facility}/eramMapFilterMenu`, (v) => v.name, signal);
    }
    async connectSio() {
        return new Promise((resolve, reject) => {
            if (this.socket) {
                if (this.socket.connected) {
                    resolve();
                    return;
                }
                this.socket.once("connect", () => {
                    resolve();
                });
                this.socket.connect();
            }
            else {
                reject("NO_SOCKET");
            }
        });
    }
    async connect() {
        if (this.usingWt) {
            // Only ERAM owns the WebTransport connection; EDST runs in the same Tauri
            // window/process and rides on ERAM's connection, so it has nothing to connect
            // here itself. This is intentionally a no-op for every other client under WT.
            if (this.client === "ERAM") {
                return await this.connectWt();
            }
        }
        else {
            return await this.connectSio();
        }
    }
    reset() {
        this._sectorId = null;
        this.dispatch?.(resetAircraftState());
        this.dispatch?.(setConnection(null));
    }
    async disconnectWt() {
        await invoke("wt_disconnect");
    }
    disconnect() {
        if (this.usingWt) {
            void this.disconnectWt();
        }
        else {
            this.socket?.disconnect();
        }
        this.reset();
    }
    connected = false;
    get disconnected() {
        return !this.connected;
    }
    get sectorId() {
        return this._sectorId;
    }
    emit(eventName, ...args) {
        if (this.usingWt) {
            if (typeof args.at(-1) === "function") {
                const cb = args.pop();
                this.emitWt(eventName, args, cb);
            }
            else {
                this.emitWt(eventName, args);
            }
        }
        else {
            this.socket?.emit(eventName, ...args);
        }
    }
    async emitWt(eventName, payload, cb) {
        let params;
        switch (payload.length) {
            case 0:
                params = null;
                break;
            case 1:
                params = payload[0];
                break;
            default:
                params = payload;
        }
        const result = await invoke("emit_nas_event", { eventName, data: JSON.stringify(params), needsReply: !!cb });
        if (!!cb && "ok" in result) {
            cb(JSON.parse(result.ok));
        }
        if (result.error) {
            console.error(`received error from wt event: ${JSON.stringify(result)}`);
        }
    }
    async signin(client) {
        console.log(`signing in`);
        return new Promise((resolve, reject) => {
            if (!this.connected) {
                return reject("NOT_CONNECTED");
            }
            this.emit("signin", client, (err) => {
                if (err) {
                    console.log(`error signing in: ${JSON.stringify(err)}`);
                    reject(err);
                    return;
                }
                // if (this.client === "ERAM") {
                //   replayManager.record();
                // }
                resolve();
            });
        });
    }
    async signout(client) {
        console.log(`signing out`);
        this._sectorId = null;
        // if (this.client === "ERAM") {
        //   replayManager.stopRecording();
        // }
        return new Promise((resolve, reject) => {
            this.emit("signout", client, (err) => {
                if (err) {
                    reject(err);
                    return;
                }
                resolve();
            });
        });
    }
    setDwellLocked(trackId, value) {
        this.emit("setDwellLocked", trackId, value);
    }
    removeAllCfrAircraft() {
        this.emit("removeAllCfrAircraft");
    }
    removeCrrGroup(groupName) {
        this.emit("removeCrrGroup", groupName);
    }
    removeAllCrrAircraftInGroup(groupName) {
        this.emit("removeAllCrrAircraftInGroup", groupName);
    }
    onConnect() {
        this.connected = true;
        this.dispatch?.(setSocketConnected(true));
        this.emit("ready", this.client);
    }
    onDisconnect() {
        this.connected = false;
        this.dispatch?.(setSocketConnected(false));
        this.dispatch?.(setConnection(null));
    }
    deleteCpdlcDialogs(arr) {
        this.emit("deleteCpdlcDialogs", arr);
    }
    deleteCdplcDialog(fpId, dialogId) {
        this.emit("deleteCpdlcDialogs", [{ fpId, dialogId }]);
    }
    sendPidDialogResponse(fpId, dialogId, response) {
        this.emit("sendPidDialogResponse", fpId, dialogId, response);
    }
    async getFrd(coord) {
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                reject(new Error("getFrd timeout"));
            }, 3e3);
            this.emit("getFrd", coord, (frd) => {
                clearTimeout(timeout);
                resolve(frd);
            });
        });
    }
    async applyPreviousRoute(fpId) {
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                reject(new Error("applyPreviousRoute timeout"));
            }, 10e3);
            this.emit("applyPreviousRoute", fpId, (result) => {
                clearTimeout(timeout);
                resolve(result);
            });
        });
    }
    async processEramMessage(elements, client, sentFromGraphicalMenu = false, skipConfirmation = false) {
        return new Promise((resolve, reject) => {
            let rejected = false;
            setTimeout(() => {
                reject(new EramMessageProcessingError(genericReject([], "REJECT - NO SYSTEM RESPONSE")));
                rejected = true;
            }, 10e3);
            this.emit("processEramMessage", { elements, client, sentFromGraphicalMenu, skipConfirmation }, (result) => {
                if (!rejected) {
                    resolve(result);
                }
            });
        });
    }
}
function eramHubConnectionFactory(url) {
    return new EramHubConnection(url);
}
export const baseEramHubConnection = eramHubConnectionFactory("");
//# sourceMappingURL=eramHubConnectionFactory.js.map